import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link, router } from '@inertiajs/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const sessionFormSchema = z.object({
  session_date: z.string().min(1, 'Session date is required'),
  duration_minutes: z.string().min(1, 'Duration is required'),
  occurred: z.boolean(),
  discussion_summary: z.string().min(1, 'Discussion summary is required'),
  mentee_todos: z.string().optional(),
  next_session_date: z.string().optional(),
});

type SessionFormData = z.infer<typeof sessionFormSchema>;

interface Match {
  id: number;
  mentor: {
    id: number;
    first_name: string;
    last_name: string;
  };
  mentee: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

interface Session {
  id: number;
  session_date: string;
  duration_minutes: number;
  occurred: boolean;
  discussion_summary: string;
  mentee_todos: string | null;
  next_session_date: string | null;
}

interface Props {
  match: Match;
  session: Session;
}

export default function EditMentorshipSession({ match, session }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SessionFormData>({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: {
      session_date: format(new Date(session.session_date), 'yyyy-MM-dd'),
      duration_minutes: session.duration_minutes.toString(),
      occurred: session.occurred,
      discussion_summary: session.discussion_summary,
      mentee_todos: session.mentee_todos || '',
      next_session_date: session.next_session_date 
        ? format(new Date(session.next_session_date), 'yyyy-MM-dd') 
        : '',
    },
  });

  const onSubmit = (data: SessionFormData) => {
    router.patch(`/matches/${match.id}/mentorship_sessions/${session.id}`, {
      mentorship_session: {
        ...data,
        duration_minutes: parseInt(data.duration_minutes),
        mentee_todos: data.mentee_todos || null,
        next_session_date: data.next_session_date || null,
      },
    });
  };

  const handleDelete = () => {
    setIsDeleting(true);
    router.delete(`/matches/${match.id}/mentorship_sessions/${session.id}`, {
      onFinish: () => {
        setIsDeleting(false);
        setShowDeleteDialog(false);
      },
    });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href={`/matches/${match.id}/mentorship_sessions`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sessions
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Session</CardTitle>
          <CardDescription>
            Update the details for this mentorship session
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="session_date">Session Date</Label>
                <Controller
                  control={control}
                  name="session_date"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  )}
                />
                {errors.session_date && (
                  <p className="text-sm text-red-600">{errors.session_date.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration_minutes">Duration</Label>
                <Controller
                  control={control}
                  name="duration_minutes"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="90">1.5 hours</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.duration_minutes && (
                  <p className="text-sm text-red-600">{errors.duration_minutes.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Controller
                  control={control}
                  name="occurred"
                  render={({ field }) => (
                    <Checkbox
                      id="occurred"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="occurred">Session occurred as planned</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discussion_summary">Discussion Summary</Label>
              <Controller
                control={control}
                name="discussion_summary"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={4}
                    placeholder="What topics were discussed?"
                  />
                )}
              />
              {errors.discussion_summary && (
                <p className="text-sm text-red-600">{errors.discussion_summary.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mentee_todos">Mentee TODOs (Optional)</Label>
              <Controller
                control={control}
                name="mentee_todos"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder="Action items for the mentee"
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="next_session_date">Next Session Date (Optional)</Label>
              <Controller
                control={control}
                name="next_session_date"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                  />
                )}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete Session
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  asChild
                >
                  <Link href={`/matches/${match.id}/mentorship_sessions`}>
                    Cancel
                  </Link>
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Updating...' : 'Update Session'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Session
            </DialogTitle>
            <DialogDescription className="pt-3 space-y-2">
              <p>Are you sure you want to delete this session?</p>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <p className="font-medium">
                  {format(new Date(session.session_date), 'MMMM d, yyyy')}
                </p>
                <p className="text-muted-foreground">
                  {session.duration_minutes} minute session
                </p>
                {session.discussion_summary && (
                  <p className="text-muted-foreground line-clamp-2">
                    "{session.discussion_summary}"
                  </p>
                )}
              </div>
              <p className="text-sm text-destructive font-medium">
                This action cannot be undone. All session data will be permanently deleted.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}